import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { electronApis } from '@/features/electron-api';
import { getIntegrationIcon } from '@/integrations/integration-icons';

import { Callout } from './callout';
import { DialogHeader } from './dialog-header';
import { FieldLabel } from './field-label';
import { ModalCard } from './modal-card';
import { DownloadRow } from './one-click-connect-modal';
import {
  oneClickProblemCopy,
  type OneClickConnectForm,
} from './use-one-click-connect';

import type { OneClickServer } from './one-click-server';
import type { OutlookSignIn } from './use-outlook-sign-in';

const CAPABILITIES = [
  {
    title: 'Calendar',
    detail: 'Read events, create and change them, and answer invitations.',
  },
  {
    title: 'Mail',
    detail:
      'Read and search mail, and write drafts for you to review. It never sends email.',
  },
];

function SignInCard({ url, code }: { url: string; code: string }) {
  const [copied, setCopied] = useState(false);
  const copyCode = () =>
    navigator.clipboard.writeText(code).then(
      () => setCopied(true),
      () => toast.error('Couldn’t copy the code')
    );

  return (
    <YStack className="gap-3 rounded-lg border border-border bg-surface p-4">
      <YStack className="gap-0.5">
        <span className="text-sm font-medium text-foreground">
          Sign in with Microsoft
        </span>
        <span className="text-xs leading-relaxed text-foreground-muted">
          Open the Microsoft sign-in page and enter this code. Sign-ins show as
          Microsoft Office.
        </span>
      </YStack>
      <XStack className="items-center gap-2">
        <span className="flex-1 font-mono text-2xl tracking-widest text-foreground">
          {code}
        </span>
        <Button variant="outline" size="sm" onClick={() => void copyCode()}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void electronApis.system.openExternal(url)}
        >
          Open sign-in page
        </Button>
      </XStack>
      <XStack className="items-center gap-2 text-xs text-foreground-muted">
        <Icons.loader className="size-3.5 animate-spin" />
        Waiting for you to finish signing in
      </XStack>
    </YStack>
  );
}

export const OutlookConnectModal = ({
  server,
  form,
  signIn,
  onRetrySignIn,
  onCancel,
  onDone,
}: {
  server: OneClickServer;
  form: OneClickConnectForm;
  signIn: OutlookSignIn | null;
  onRetrySignIn: () => void;
  onCancel: () => void;
  onDone: () => void;
}): React.ReactElement => {
  const IntegrationIcon = getIntegrationIcon(server.id);
  const connected = form.phase === 'connected';

  return (
    <ModalCard onClose={onCancel} closeDisabled={!form.canCancel}>
      <DialogHeader
        icon={<IntegrationIcon className="size-5" />}
        title={`Connect ${server.name}`}
        subtitle={<span className="block">{server.description}</span>}
      />
      <YStack className="gap-5 p-6">
        <YStack className="gap-2">
          <FieldLabel>Littlebird will be able to</FieldLabel>
          <YStack className="divide-y divide-border rounded-lg border border-border">
            {CAPABILITIES.map((capability) => (
              <YStack key={capability.title} className="gap-0.5 px-4 py-3">
                <span className="text-sm font-medium text-foreground">
                  {capability.title}
                </span>
                <span className="text-xs leading-relaxed text-foreground-muted">
                  {capability.detail}
                </span>
              </YStack>
            ))}
          </YStack>
        </YStack>

        <YStack className="gap-2">
          <FieldLabel>Microsoft account</FieldLabel>
          {form.phase === 'preparing' && (
            <YStack className="gap-2">
              <span className="text-sm text-foreground">
                Getting Outlook ready on this Mac
              </span>
              <DownloadRow form={form} />
            </YStack>
          )}
          {form.problem && (
            <Callout tone="danger" icon={<Icons.warn className="size-4" />}>
              {oneClickProblemCopy(form.problem, server.name)}
            </Callout>
          )}
          {signIn?.state === 'failed' && (
            <Callout tone="danger" icon={<Icons.warn className="size-4" />}>
              Littlebird couldn’t reach Outlook to sign you in. Try again.
            </Callout>
          )}
          {signIn?.state === 'code' && (
            <SignInCard key={signIn.code} url={signIn.url} code={signIn.code} />
          )}
          {signIn?.state === 'signed_in' && (
            <XStack className="items-center gap-2.5 rounded-lg border border-success-6 bg-success-3 px-3 py-2.5">
              <Icons.check className="size-4 shrink-0 text-success-11" />
              <span className="text-sm text-foreground">
                {signIn.email
                  ? `Signed in as ${signIn.email}`
                  : 'Signed in to Microsoft'}
              </span>
            </XStack>
          )}
          <span className="text-xs text-foreground-muted">
            Your sign-in stays in this Mac’s keychain.
          </span>
        </YStack>
      </YStack>
      <XStack className="items-center justify-end gap-2 border-t border-border p-4 px-6">
        <Button
          variant="ghost"
          size="sm"
          disabled={!form.canCancel}
          onClick={onCancel}
        >
          Cancel
        </Button>
        {signIn?.state === 'failed' ? (
          <Button variant="default" size="sm" onClick={onRetrySignIn}>
            Try again
          </Button>
        ) : connected ? (
          <Button
            variant="default"
            size="sm"
            disabled={signIn?.state !== 'signed_in'}
            isLoading={!signIn}
            loadingText="Connecting…"
            onClick={onDone}
          >
            Done
          </Button>
        ) : (
          <Button
            variant="default"
            size="sm"
            disabled={!form.canConnect}
            isLoading={form.busy}
            loadingText={
              form.phase === 'preparing' ? 'Getting ready…' : 'Connecting…'
            }
            onClick={() => void form.connect()}
          >
            {form.problem ? 'Try again' : 'Connect'}
          </Button>
        )}
      </XStack>
    </ModalCard>
  );
};
